import classNames from 'classnames'
import {Formik} from 'formik'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import moment from 'moment'
import {SimpleBlueButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import Input from '../../components/core/input'
import Footer from '../../components/layouts/footer'
import VoicePreview from '../../components/pages/phoneCalls/VoicePreview'
import incidentVariableExamples from '../../data/incidentVariableExamples.json'
import languages from '../../data/languages.json'
import {FemaleIcon, MaleIcon} from '../../icons'
import {routes} from '../../routes'
import {convertTexttoSpeech, createNewPhoneCallTemplate, getAzureVoicesList, getPhoneCallTemplates, setPhoneCallTemplates, updatePhoneCallTemplate} from '../../stores/phone-call'
import {combineAndFormat, DateFormats, formatDate} from '../../utils/date'
import NavigationHelper from '../../utils/navigation'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {replaceVariablesWithExamples, transformTextWithVariables, trimStringValues} from '../../utils/text'
import {phoneCallTemplateSchema} from '../../utils/validation'
import ContentHeader from '../../components/layouts/header'
import VoiceSettingsRow from '../../components/pages/phoneCalls/VoiceSettings'
import VariablesPanel from '../../components/pages/phoneCalls/VariablesPanel'
import './index.scss'
import PhoneCallActionBar from '../../components/pages/phoneCalls/PhoneCallActionBar'
import MentionTextarea from '../../components/pages/phoneCalls/MentionTextarea'
import CustomSelect from '../../components/core/customSelect'
import {getTeams} from '../../stores/teams'
import ForPaid from '../../components/core/forPaid'

const MAX_TEXT_LENGTH = 300
const INITIAL_VISIBLE_VARIABLES = 2

const languagesList = Object.values(languages)
const variableOptions = Object.values(incidentVariableExamples)

const getVoicesWithIcons = (voicesList, language) =>
    voicesList.map((voice) => ({
        ...voice,
        icon: voice.gender === 'Female' ? <FemaleIcon /> : <MaleIcon />
    }))

const getFilteredVoices = (voicesList, language) => voicesList.filter((voice) => voice.locale === language)

const CreateEditPhoneCall = () => {
    const dispatch = useDispatch()
    const formikRef = useRef()
    const navigate = useNavigate()
    const {id: phoneCallId} = useParams()

    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const voicesList = useSelector(state => state.phone_call.voicesList)
    const phoneCallTemplates = useSelector(state => state.phone_call.phoneCallTemplates)
    const teams = useSelector(state => state.teams.teams)
    const isSidebarActive = useSelector(state => state.sidebar.sidebarIsActive)

    const text = useNamespaceTranslations('phone-calls')
    const formFieldText = useNamespaceTranslations('forms')?.form_values

    const currentDate = formatDate(new Date(), DateFormats.SLASH_DMY_TIME)
    const [startTime, setStartTime] = useState(moment())
    const [isFirstLoad, setIsFirstLoad] = useState(true)

    const currentPhoneCallTemplate = phoneCallTemplates?.find(phoneCallTemplate => phoneCallTemplate?.id === phoneCallId)
    const currentPhoneCallTemplateLocale = languagesList?.find(lang => lang?.value === currentPhoneCallTemplate?.locale)

    const initialVariableOptions = useMemo(() =>
        variableOptions.map(varOption =>
            varOption.type === 'date'
                ? {...varOption, default_value: currentDate}
                : varOption
        ), []
    )

    const voicesListWithIcons = getVoicesWithIcons(voicesList)

    const initialFormValues = useMemo(() => ({
        name: currentPhoneCallTemplate ? currentPhoneCallTemplate?.name : '',
        description: currentPhoneCallTemplate ? currentPhoneCallTemplate?.description : '',
        teams: teams?.length > 0 ? teams.filter(team => currentPhoneCallTemplate ? currentPhoneCallTemplate?.assignees?.some(assignee => team?.team_id === assignee?.id) : team.is_member) : [],
        language: currentPhoneCallTemplate ? currentPhoneCallTemplateLocale : languages.us,
        voice: currentPhoneCallTemplate ? voicesListWithIcons?.find(voice => voice?.value === currentPhoneCallTemplate.voice) : getFilteredVoices(voicesListWithIcons, languages.us.value)?.[0],
        text: {
            markup: currentPhoneCallTemplate ? transformTextWithVariables(currentPhoneCallTemplate?.text, incidentVariableExamples) : '',
            plain: currentPhoneCallTemplate ? currentPhoneCallTemplate?.text : ''
        }
    }), [voicesList, text, currentPhoneCallTemplate])

    const [isTextareaFocused, setIsTextareaFocused] = useState(false)
    const [isPreviewMode, setIsPreviewMode] = useState(false)
    const [variables, setVariables] = useState(initialVariableOptions)
    const [showMoreVariables, setShowMoreVariables] = useState(false)
    const [showVariablesPanel, setShowVariablesPanel] = useState(false)
    const [audioUrl, setAudioUrl] = useState('')
    const [isLoadingAudio, setIsLoadingAudio] = useState(false)
    const [isProcessingData, setIsProcessingData] = useState(false)
    const [lastAudioRequestKey, setLastAudioRequestKey] = useState(null)

    const visibleVariables = useMemo(() => variables.slice(0, INITIAL_VISIBLE_VARIABLES), [variables])
    const hasMoreVariables = useMemo(() => variables.length > INITIAL_VISIBLE_VARIABLES, [variables.length])

    const resetForm = useCallback(() => {
        formikRef?.current?.resetForm()
        setVariables(initialVariableOptions)
        setShowMoreVariables(false)
        setShowVariablesPanel(false)
        setAudioUrl('')
        setLastAudioRequestKey(null)
    }, [initialVariableOptions])

    const togglePreviewMode = useCallback(() => {
        setIsPreviewMode((prev) => !prev)
    }, [])

    const toggleVariablesPanel = useCallback(() => {
        setShowVariablesPanel((prev) => !prev)
        setShowMoreVariables(false)
    }, [])

    const toggleShowAllVariables = useCallback(() => {
        setShowMoreVariables((prev) => !prev)
    }, [])

    const handleMentionChange = useCallback((_, newMarkup, newPlainText) => {
        if (newPlainText?.length > MAX_TEXT_LENGTH) return

        formikRef.current?.setFieldValue(formFieldText.text.key, {
            markup: newMarkup,
            plain: newPlainText
        })
    }, [formFieldText?.text?.key])

    const handleLanguageChange = useCallback((_, selectedLanguage) => {
        const filteredVoices = getFilteredVoices(voicesListWithIcons, selectedLanguage.value)
        formikRef.current?.setFieldValue(formFieldText.language.key, selectedLanguage)
        formikRef.current?.setFieldValue(formFieldText.voice.key, filteredVoices[0])
    }, [voicesList, formFieldText?.language?.key, formFieldText?.voice?.key])

    const handleVariableValueChange = useCallback((key, value) => {
        const formattedTime = combineAndFormat(value, startTime, DateFormats.SLASH_DMY_TIME)

        setVariables((prev) =>
            prev.map((varOption) =>
                varOption.key === key
                    ? {
                        ...varOption,
                        default_value:
                                  varOption.type === incidentVariableExamples.last_received_at.type ? formattedTime : value
                    }
                    : varOption
            )
        )
    }, [startTime])

    const handleStartTimeChange = useCallback((key, time) => {
        setStartTime(time)
        setVariables((prev) => {
            const variable = prev.find((v) => v.key === key)
            const newValue = combineAndFormat(variable?.default_value || currentDate, time, DateFormats.SLASH_DMY_TIME)

            return prev.map((item) =>
                item?.type === incidentVariableExamples.last_received_at.type && key === item.key ? {...item, default_value: newValue} : item
            )
        })
    }, [])

    const handlePlayVoice = useCallback(async() => {
        const {voice, text, language} = formikRef.current.values

        if (!voice?.value || !text?.markup?.trim()) return

        const finalVariables = variables.map(variable => variable.type === incidentVariableExamples.state_current.type ?
            ({...variable, default_value: variable.default_value.toLowerCase()})
            : variable)

        const resolvedText = replaceVariablesWithExamples(text, finalVariables)
        const requestKey = `${language.value}_${voice.value}_${resolvedText}`

        if (requestKey === lastAudioRequestKey && audioUrl) return

        setIsLoadingAudio(true)
        setLastAudioRequestKey(requestKey)

        try {
            const payload = {
                voice: voice.value,
                locale: language.value,
                text: resolvedText
            }

            const result = await dispatch(convertTexttoSpeech(payload))
            setAudioUrl(result)
        } finally {
            setIsLoadingAudio(false)
        }
    }, [audioUrl, lastAudioRequestKey, variables, dispatch])

    const handleSubmit = useCallback(async(values, {setFieldError}) => {
        setIsProcessingData(true)

        const modifiedVariables = variables.filter((varOption) => {
            const original = initialVariableOptions.find((initial) => initial.key === varOption.key)
            return original
        })

        const payload = {
            name: values.name,
            description: values.description,
            locale: values.language.value,
            voice: values.voice.value,
            text: values.text.plain,
            assignees: values.teams.length > 0 ? values.teams.map(team => ({type: 'Team', id: team.team_id})) : [],
            ...(modifiedVariables?.length > 0 && {
                variable_examples: modifiedVariables.reduce((acc, mv) => {
                    acc[mv.key] = mv.default_value
                    return acc
                }, {})
            })
        }

        try {
            const data = phoneCallId ? await dispatch(updatePhoneCallTemplate(phoneCallId, trimStringValues(payload), setFieldError)) : await dispatch(createNewPhoneCallTemplate(trimStringValues(payload), setFieldError))
            if (data?.success) {
                navigate(NavigationHelper.getLocalizedPath(routes.phone_calls.paths))
            }
        } finally {
            setIsProcessingData(false)
        }
    }, [dispatch, navigate, variables, initialVariableOptions, phoneCallId])

    const handleCancel = useCallback(() => {
        navigate(NavigationHelper.getLocalizedPath(routes.phone_calls.paths))
    }, [navigate])

    useEffect(() => {
        if (currentPhoneCallTemplate) {
            setVariables(initialVariableOptions?.map(varOption => currentPhoneCallTemplate?.variable_examples?.[varOption?.key] ?
                ({...varOption, default_value: currentPhoneCallTemplate.variable_examples[varOption.key]}) :
                varOption
            ))
        }
    }, [currentPhoneCallTemplate])

    useEffect(() => {
        dispatch(getAzureVoicesList())
        dispatch(getTeams())
        dispatch(setPhoneCallTemplates(null))
        setIsFirstLoad(false)
    }, [dispatch])

    useEffect(() => {
        if (isFirstLoad) dispatch(getPhoneCallTemplates())
    }, [isFirstLoad])

    useEffect(() => {
        const formik = formikRef.current
        if (!formik || !Object.keys(formik.errors).length) return

        (async() => {
            const schemaErrors = await formik.validateForm(formik.values)
            const current = formik.errors || {}

            const serverOnly = {}
            for (const k of Object.keys(current)) {
                if (!schemaErrors || !(k in schemaErrors)) {
                    serverOnly[k] = current[k]
                }
            }

            formik.setErrors({...(schemaErrors || {}), ...serverOnly})
        })()
    }, [text])

    const isDisabled = !phoneCallId && phoneCallTemplates?.length === 1

    return (
        <main className={classNames('main phone-calls', {dark: isDarkMode})}>
            <div>
                <ContentHeader section={text.section} title={phoneCallId ? text.edit_phone_call : text.create_phone_call} />

                {isDisabled && <ForPaid forPaidText={text.limit_info_text} className='limit-info' isExceeded />}

                <div className={classNames('phone-call-container', {dark: isDarkMode})}>
                    <span className="phone-call-details">{text.details}</span>

                    <Formik
                        initialValues={initialFormValues}
                        enableReinitialize
                        validateOnBlur={false}
                        validateOnChange={false}
                        innerRef={formikRef}
                        validationSchema={phoneCallTemplateSchema}
                        onSubmit={handleSubmit}
                    >
                        {({errors, handleChange, values, setFieldValue, handleSubmit: formikHandleSubmit}) => {
                            const filteredVoicesForLanguage = useMemo(
                                () => getFilteredVoices(voicesListWithIcons, values?.language?.value),
                                [voicesList, values?.language?.value]
                            )

                            return (
                                <form className="phone-call-form" onSubmit={formikHandleSubmit}>
                                    <Input
                                        name={formFieldText.name.key}
                                        value={values.name}
                                        error={errors.name}
                                        placeholder={formFieldText.name.placeholder}
                                        infoText={formFieldText.name.info}
                                        onChange={handleChange}
                                        inputIsDisabled={isDisabled}
                                    />

                                    <Input
                                        name={formFieldText.description.key}
                                        value={values.description}
                                        error={errors.description}
                                        placeholder={formFieldText.description.placeholder}
                                        infoText={formFieldText.description.info}
                                        onChange={handleChange}
                                        inputIsDisabled={isDisabled}
                                    />

                                    <CustomSelect
                                        name={formFieldText.teams.key}
                                        value={values.teams}
                                        onChange={setFieldValue}
                                        placeholder={formFieldText.teams.placeholder}
                                        infoText={formFieldText.teams.info}
                                        options={teams}
                                        error={errors.teams}
                                        isDisabled={isDisabled}
                                        isMulti
                                        isClearable
                                    />

                                    <span
                                        className={classNames('form-section-label voice-settings-label', {
                                            dark: isDarkMode
                                        })}
                                    >
                                        {text.voice_settings}
                                    </span>

                                    <VoiceSettingsRow
                                        languageValue={values.language}
                                        voiceValue={values.voice}
                                        languageOptions={languagesList}
                                        voiceOptions={filteredVoicesForLanguage}
                                        languageError={errors.language}
                                        voiceError={errors.voice}
                                        formFieldText={formFieldText}
                                        onLanguageChange={handleLanguageChange}
                                        onVoiceChange={setFieldValue}
                                        isSidebarActive={isSidebarActive}
                                        isDisabled={isDisabled}
                                    />

                                    <MentionTextarea
                                        values={values}
                                        errors={errors}
                                        isPreviewMode={isPreviewMode}
                                        isTextareaFocused={isTextareaFocused}
                                        isDarkMode={isDarkMode}
                                        variables={variables}
                                        isDisabled={isDisabled}
                                        text={text}
                                        formFieldText={formFieldText}
                                        onMentionChange={handleMentionChange}
                                        onFocus={() => setIsTextareaFocused(true)}
                                        onBlur={() => setIsTextareaFocused(false)}
                                    />

                                    <PhoneCallActionBar
                                        text={text}
                                        isDarkMode={isDarkMode}
                                        isPreviewMode={isPreviewMode}
                                        isLoadingAudio={isLoadingAudio}
                                        onToggleVariables={toggleVariablesPanel}
                                        onReset={resetForm}
                                        onTogglePreview={togglePreviewMode}
                                        onPlayVoice={handlePlayVoice}
                                        isDisabled={isDisabled}
                                    />

                                    {showVariablesPanel && visibleVariables?.length > 0 && (
                                        <VariablesPanel
                                            variables={variables}
                                            visibleVariables={visibleVariables}
                                            hasMoreVariables={hasMoreVariables}
                                            showMoreVariables={showMoreVariables}
                                            isDarkMode={isDarkMode}
                                            text={text}
                                            startTime={startTime}
                                            onVariableChange={handleVariableValueChange}
                                            onStartTimeChange={handleStartTimeChange}
                                            onToggleShowAll={toggleShowAllVariables}
                                        />
                                    )}

                                    <VoicePreview
                                        audioUrl={audioUrl}
                                        isDarkMode={isDarkMode}
                                        isLoadingAudio={isLoadingAudio}
                                    />

                                    <span className="form-actions">
                                        <SimpleBlueButton type="submit" disabled={isProcessingData || isDisabled}>
                                            {isProcessingData && <ButtonLoader />}
                                            {text.save}
                                        </SimpleBlueButton>

                                        <SimpleBlueButton className="cancel-button" type="button" onClick={handleCancel}>
                                            {text.cancel}
                                        </SimpleBlueButton>
                                    </span>
                                </form>
                            )
                        }}
                    </Formik>
                </div>
            </div>

            <Footer />
        </main>
    )
}

export default CreateEditPhoneCall
